import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, RefreshCw, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { adminLogQueries } from '@/api/endpoints/admin-logs'
import {
  deleteProfile,
  profileAccountsUnknown,
  profileHasAccounts,
  profileMayDelete,
  profileQueries,
  refreshProfile,
  updateProfile,
} from '@/api/endpoints/profiles'
import { apiErrorMessage } from '@/api/errors'
import { ConfirmDialog } from '@/components/kit/confirm-dialog'
import { Form } from '@/components/kit/form-fields'
import { RoleGate } from '@/components/kit/role-gate'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AuditPanel } from '@/features/audit/audit-panel'
import { profileFormSchema, type ProfileFormValues } from '@/features/profiles/profile-form-schema'
import { ProfileDetailsFields } from '@/features/profiles/profile-details-fields'
import { toProfileFormValues, toProfileUpdateInput } from '@/features/profiles/to-update-input'
import { ProfileMembershipsPanel } from '@/features/profiles/profile-memberships-panel'
import { ProfileRiotAccountsPanel } from '@/features/profiles/profile-riot-accounts-panel'
import { ProfileSocialMediaPanel } from '@/features/profiles/profile-social-media-panel'

export const Route = createFileRoute('/_authed/profiles/$uuid')({
  loader: ({ context, params }) =>
    context.queryClient.prefetchQuery(profileQueries.detail(params.uuid)),
  component: ProfileEditPage,
})

const profilesRoute = getRouteApi('/_authed/profiles')

function ProfileEditPage() {
  const { uuid } = Route.useParams()
  const listSearch = profilesRoute.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: profile, isLoading, isError, error } = useQuery(profileQueries.detail(uuid))
  const [confirmDelete, setConfirmDelete] = useState(false)

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      name: '',
      country: '',
      otherCountries: [],
      playerPosition: null,
      staffPosition: null,
    },
  })

  const { isDirty } = form.formState

  useEffect(() => {
    if (profile && !isDirty) form.reset(toProfileFormValues(profile))
  }, [profile, form, isDirty])

  const save = useMutation({
    mutationFn: (values: ProfileFormValues) => {
      if (!profile) throw new Error('Profile not loaded')
      return updateProfile(uuid, toProfileUpdateInput(profile, values))
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(profileQueries.detail(uuid).queryKey, updated)
      void queryClient.invalidateQueries({ queryKey: profileQueries.all })
      void queryClient.invalidateQueries({ queryKey: adminLogQueries.entity(uuid).queryKey })
      toast.success('Profile saved')
      form.reset(toProfileFormValues(updated))
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  })

  const refresh = useMutation({
    mutationFn: () => refreshProfile(uuid),
    onSuccess: (updated) => {
      queryClient.setQueryData(profileQueries.detail(uuid).queryKey, updated)
      toast.success('Profile cache cleared')
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  })

  const remove = useMutation({
    mutationFn: () => deleteProfile(uuid),
    onSuccess: async () => {
      void queryClient.invalidateQueries({ queryKey: profileQueries.all })
      toast.success('Profile deleted')
      await navigate({ to: '/profiles', search: listSearch })
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full max-w-xl" />
      </div>
    )
  }

  if (isError || !profile) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" asChild>
          <Link to="/profiles" search={listSearch}>
            <ArrowLeft />
            Back to profiles
          </Link>
        </Button>
        <p role="alert" className="text-destructive">
          {apiErrorMessage(error ?? new Error('Profile not found'))}
        </p>
      </div>
    )
  }

  const accountsUnknown = profileAccountsUnknown(profile)
  const hasLinkedAccounts = profileHasAccounts(profile)
  const canDelete = profileMayDelete(profile)
  const playerPositionDescription = accountsUnknown
    ? 'Riot account linkage is unknown until the show payload includes accounts.'
    : hasLinkedAccounts
      ? 'Clearing the player role is blocked while Riot accounts are linked.'
      : undefined

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/profiles" search={listSearch}>
            <ArrowLeft />
            Profiles
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">{profile.name}</h1>
      </div>

      <Form form={form} onSubmit={(values) => void save.mutateAsync(values)} className="grid gap-4">
        <ProfileDetailsFields
          lockPlayerPosition={accountsUnknown || hasLinkedAccounts}
          playerPositionDescription={playerPositionDescription}
        />

        <div className="flex flex-wrap gap-2 pt-2">
          <Button type="submit" disabled={save.isPending || !form.formState.isDirty}>
            Save
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={refresh.isPending}
            onClick={() => void refresh.mutateAsync()}
          >
            <RefreshCw />
            Refresh cache
          </Button>
          {canDelete && (
            <RoleGate role="ROLE_ADMIN">
              <Button type="button" variant="destructive" onClick={() => setConfirmDelete(true)}>
                <Trash2 />
                Delete
              </Button>
            </RoleGate>
          )}
        </div>
      </Form>

      <ProfileSocialMediaPanel profileUuid={uuid} />
      <ProfileMembershipsPanel profileUuid={uuid} />
      <ProfileRiotAccountsPanel profile={profile} />
      <RoleGate role="ROLE_SUPER_ADMIN">
        <AuditPanel entityUuid={uuid} description="Logged changes to this profile" />
      </RoleGate>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this profile?"
        description="This soft-deletes the profile. It can be restored from trash later."
        confirmLabel="Delete"
        destructive
        onConfirm={() => remove.mutateAsync()}
      />
    </div>
  )
}
